import type { Result } from '@ankor-io/common/lang/types'
import { AWXConnectedAccountStatus, AwxConnectedAccountType } from '@ankor-io/common/types/awx'

export type Account = {
  id: string
  organizations: Record<string, AccountOrganization>
  permissions: string[]
}

export enum Theme {
  LIGHT = 'light',
  DARK = 'dark',
}

export enum OnboardingStatus {
  NOT_STARTED = 'not_started', // Not entered org name
  ORG_CREATED = 'org_created', // Entered org name, vessel step pending
  VESSEL_CREATED = 'vessel_created', // Vessel created, user invite step pending
  COMPLETED = 'completed', // Created connected account
}

/**
 * Controls whether an organization is visible in the user's organization list.
 * Used to hide default organizations when users are invited to other organizations.
 */
export enum OrganizationVisibility {
  /**
   * Organization appears in user's organization list
   * */
  VISIBLE = 'visible',
  /**
   * Organization is hidden from user's organization list (but user still belongs to it)
   * */
  HIDDEN = 'hidden',
}

export enum AccountPreferenceKeys {
  ACTIVE_CONNECTED_ACCOUNT_ID = 'activeConnectedAccountId',
  ACTIVE_ORGANIZATION_ID = 'activeOrganizationId',
  ACTIVE_VESSEL_ID = 'activeVesselId',
  ONBOARDING_STATUS = 'onboardingStatus',
  THEME = 'theme',
  SHOW_WALLET_INSTRUCTIONS = 'showWalletInstructions',
}

export type AccountPreferences = {
  [AccountPreferenceKeys.ACTIVE_CONNECTED_ACCOUNT_ID]?: string
  [AccountPreferenceKeys.ACTIVE_ORGANIZATION_ID]?: string
  [AccountPreferenceKeys.ACTIVE_VESSEL_ID]?: string
  [AccountPreferenceKeys.ONBOARDING_STATUS]?: string
  [AccountPreferenceKeys.THEME]?: string
  [AccountPreferenceKeys.SHOW_WALLET_INSTRUCTIONS]?: string
}

export type Me = Account & {
  organizations: OrganizationsDetail
  preferences: AccountPreferences | null
}

export type AccountOrganization = {
  id: string
  roles: Role[]
}

export type OrganizationsDetail = Record<string, OrganizationDetail>

export type OrganizationDetail = AccountOrganization & {
  name: string
  description: string
  /**
   * Controls visibility in user's organization list
   */
  visibility: OrganizationVisibility
}

export type OrganizationLighthouse = {
  id: string
  name: string
  description: string
  userCount: number
  visibility: OrganizationVisibility
}

export type Identity = {
  accountId: string
  firstName: string
  lastName: string
  email: string
  lastSignedIn?: string
}

/**
 * The roles are ordered from the highest to the lowest.
 * If you have to enter a new role, add it in the correct position considering the order dictates the hierarchy.
 */
export enum Role {
  OWNER = 'owner',
  ADMIN = 'admin',
  VESSEL_ADMIN = 'vessel_admin',
  APPROVER = 'approver',
  SPENDER = 'spender',
  VIEWER = 'viewer',
}

export type Organization = {
  id: string
  name: string
  description: string
  /**
   * Controls visibility in user's organization list
   */
  visibility: OrganizationVisibility
}

export type ListOrganizationsRequest = {
  take?: number
  skip?: number
  visibility?: OrganizationVisibility[] // Filter by specific visibility values
  organizationName?: string
  organizationId?: string
  vesselName?: string
}

/**
 * Represents an invitation to join an organization and optionally one or more connected accounts.
 *
 * Note: `awxConnectedAccountId` is deprecated - use `awxConnectedAccountIds` instead.
 * The singular field is kept for backwards compatibility with old pending invites.
 */
export type Invite = {
  id: string
  /** Email address of the invitee */
  email: string
  status: InviteStatus
  /** Roles to assign to the invitee upon accepting */
  roles: Role[]
  createdAt: string
  /** User ID of the person who sent the invite */
  invitedBy: string
  organization_id: string
  /** @deprecated Use awxConnectedAccountIds instead. Kept for backwards compatibility with old pending invites. */
  awxConnectedAccountId: string | null
  /** List of connected account IDs the invitee will be added to upon accepting */
  awxConnectedAccountIds: string[]
  /** The vessel this invite is associated with (used by vessel invite endpoint) */
  vesselId: string | null
  // the sendgrid message id tracking (used to track the status of the invite email)
  messageId?: string
  // present if the sendgrid email failed to send
  reason?: string
}

export enum InviteStatus {
  SENDING = 'sending',
  SENT = 'sent',
  FAILED_TO_SEND = 'failed_to_send',
  PENDING = 'pending',
  ACCEPTED = 'accepted',
  REJECTED = 'rejected',
  EXPIRED = 'expired',
  SIGNING_UP = 'signing_up',
}

export enum InviteResult {
  EXPIRED = 'expired',
  NOT_PENDING = 'not_pending',
  NOT_FOUND = 'not_found',
  ACCEPTED = 'accepted',
  ALREADY_ACCEPTED = 'already_accepted',
}

/**
 * Returns true if the status is a user action status
 * @param status the status to check
 */
export const userActionInviteStatus = (status: InviteStatus): boolean => {
  return [
    InviteStatus.ACCEPTED, //
    InviteStatus.REJECTED,
    InviteStatus.SIGNING_UP,
    InviteStatus.EXPIRED,
  ].includes(status)
}

/**
 * Data returned when accepting an invite.
 * Used by both frontend and backend for type safety.
 */
export type InviteResponseData = {
  requireSignup?: boolean
  inviteId: string
  organization: Organization
  invitedByEmail: string
  inviteeEmail: string
}

/**
 * Result type for invite acceptance operations.
 * Reusable across frontend and backend.
 */
export type InviteResponseResult = Result<InviteResponseData, InviteResult>

/**
 * This is the new organization user payload type for the listing endpoint supporting the new vessel based access control.
 */
export type OrganizationUserPayload = {
  // the user identity
  identity: Identity
  // the list of roles
  roles: Role[]
  // the list of vessels the user has access to
  vessels?: OrganizationUserVesselPayload[]
}

export type OrganizationUserVesselPayload = {
  // the vessel id
  id: string
  // the vessel name
  name: string
  // the list of connected accounts the user has access to within the vessel
  connectedAccounts: OrganizationUserConnectedAccountPayload[]
}

export type OrganizationUserConnectedAccountPayload = {
  // the connected account id
  id: string
  // the connected account name
  name: string
  // the connected account status
  status: AWXConnectedAccountStatus
  // the connected account type
  type: AwxConnectedAccountType
}

// internal type used by the backend
export type RawOrganizationUser = {
  id: string
  roles: {
    roles: string[]
  }[]
  UserConnectedAwxAccount: {
    awxAccount: {
      id: string
      name: string
      status: string
      type: string
      vessel: {
        id: string
        name: string
      } | null
    }
  }[]
  UserVessel: {
    vessel: {
      id: string
      name: string
      awxConnectedAccounts: {
        id: string
        name: string
        status: string
        type: string
      }[]
    }
  }[]
}

export type ListOrganizationUsersWithAccessArgs = {
  organizationId: string
  take: number
  skip: number
}
